/**
 * The single name / date-of-birth comparison behind both identity-mismatch
 * surfaces: the advisory mismatch chips on a Request Detail search-result card
 * and the Confirm & Link modal's manual-override gate. They must agree — if the
 * card says a field matches while the modal demands an audit-logged override
 * (or the reverse), staff are being told two different things about the same
 * pair of records.
 */

/**
 * Names are compared case-insensitively with surrounding and internal
 * whitespace collapsed, so "Jane  Doe" and "Jane Doe" are the same name. Any
 * other difference — a middle name, an initial, a different spelling — is a
 * mismatch: this comparison is deliberately literal, because widening it is
 * what would let a wrong record through as a "match".
 */
function normalizeName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * True when the submitted name differs from the matched patient record's.
 *
 * A blank on either side is always a mismatch, never a match: the API omits the
 * submitted name for some requests, and two names that are both unknown are not
 * evidence that they agree. Comparing the normalized strings alone would make
 * blank-vs-blank read as a match, which would clear the Confirm & Link override
 * gate — linking a record with no name verification and nothing in the audit log
 * recording that. Fail closed and let staff justify it instead.
 */
export function isNameMismatch(
  submittedName: string | null | undefined,
  patientName: string | null | undefined,
): boolean {
  const submitted = normalizeName(submittedName ?? '');
  const matched = normalizeName(patientName ?? '');
  if (!submitted || !matched) {
    return true;
  }
  return submitted !== matched;
}

/**
 * Compares the calendar-date portion of both values, which assumes the two
 * sides use the same serialization — they come from the same API, as either a
 * date-only string ('1987-02-14') or UTC midnight ('…T00:00:00Z'). If the
 * payload ever carries a real UTC offset ('1987-02-13T19:00:00-05:00'), revisit
 * this: the literal date part would then differ from the calendar date the
 * backend means. Note the direction of that error — splitting on 'T' can only
 * produce a false *mismatch*, never a false match, which is the right way to be
 * wrong on an identity check.
 */
function normalizeDateOfBirth(value: string): string {
  return value.split('T')[0];
}

/** True when the submitted date of birth differs from the patient record's. */
export function isDateOfBirthMismatch(submittedDob: string, patientDob: string): boolean {
  return normalizeDateOfBirth(submittedDob) !== normalizeDateOfBirth(patientDob);
}

/**
 * Whether the request actually submitted an MRN. Whitespace-only counts as none, so every surface
 * that asks — the mismatch check, the identifier display, the "consistent fields" banner — agrees on
 * whether there was an MRN to compare. A raw truthiness check would let `'  '` claim an MRN
 * comparison that never ran.
 */
export function hasSubmittedMedicalRecordNumber(mrn: string | null | undefined): boolean {
  return (mrn ?? '').trim().length > 0;
}

/**
 * True when a submitted MRN fails to corroborate the patient record's.
 *
 * Unlike name and date of birth, a *missing* submitted MRN is **not** a mismatch: most requests
 * arrive without one, and treating absence as a discrepancy would put an override justification in
 * front of every ordinary link. Absence means "nothing to check here", so the caller falls back to
 * the name and date of birth it does have.
 *
 * A submitted MRN with no counterpart on the record *is* a mismatch, though. The staff member gave
 * an identifier the record cannot corroborate, and on an identity check an unanswered claim is not
 * the same as an agreeing one — the same reason a blank name fails closed above.
 *
 * Compared case-insensitively with surrounding whitespace trimmed, because an MRN is an opaque
 * external identifier and casing is not part of its identity. Note the search filters MRN with a
 * *substring* match, so a record can come back from the search without its MRN being this record's
 * MRN — which is exactly why the comparison is made again here rather than inferred from the hit.
 */
export function isMedicalRecordNumberMismatch(
  submittedMrn: string | null | undefined,
  patientMrn: string | null | undefined,
): boolean {
  if (!hasSubmittedMedicalRecordNumber(submittedMrn)) {
    return false;
  }

  const submitted = (submittedMrn ?? '').trim().toLowerCase();
  const matched = (patientMrn ?? '').trim().toLowerCase();
  return submitted !== matched;
}
